import type {
  InteractiveContentChoice as EditorInteractiveContentChoice,
  StaticContentEntity,
} from '@core-api/editor/types/response';
import type { QuestionField } from '@mentimeter/legacy-series-types/common';
import type { Question } from '@mentimeter/legacy-series-types/question';
import type { Slide as VotingSlide } from '../../compatibility-overrides/voting-types';
import type { CompatibilitySlide } from '../../compatibility-types';
import { getOrSetCache, type WithCache } from '../../utils/get-or-set-cache';
import { MismatchedSlideTypePropertyError } from '../errors/mismatched-slide-type-property-error';
import type { CommonQuestionProperty } from './get-common-question-property';

const StaticContentEntityType: StaticContentEntity['type'] = 'quick-form';

export const getQuestionQuickFormProperty = (
  target: CompatibilitySlide<'quick-form'> | VotingSlide<'quick-form'>,
  prop: keyof Question,
) => {
  const interactiveContents = target.interactiveContents?.[0];

  const staticContents = target.staticContent;

  if (prop === 'question_styled') {
    return staticContents.styledTitle ?? {};
  }

  if (interactiveContents) {
    switch (prop as keyof Omit<Question, CommonQuestionProperty>) {
      case 'fields': {
        const fields = interactiveContents.choices.map<QuestionField>(
          (choice, i) => {
            const responseSettings = (choice as EditorInteractiveContentChoice)
              .responseSettings;
            return {
              position: i,
              id: choice.legacyQuestionMetadataFieldId ?? 0,
              label: choice.title,
              type: responseSettings?.format ?? 'text',
              options: responseSettings?.options ?? [],
              is_required: responseSettings?.policy === 'response-required',
              delete: undefined,
            };
          },
        );

        const cacheKey = JSON.stringify(fields);
        return getOrSetCache(
          target as WithCache<CompatibilitySlide>,
          'fields',
          cacheKey,
          fields,
        );
      }
      default:
        return undefined;
    }
  }
  throw new MismatchedSlideTypePropertyError(
    StaticContentEntityType,
    target.staticContent.type,
  );
};
